import { withDependencies } from '@wix/thunderbolt-ioc'
import { ViewMode as ViewModeType } from '@wix/thunderbolt-becky-types'
import type { EditorViewModeState } from './types'
import type { DSCarmi } from 'thunderbolt-ds-carmi-root'
import { getBaseStructure } from 'thunderbolt-viewer-manager-types'
import {
	CarmiInstance,
	NavigationInfoSymbol,
	RenderDoneObserverSymbol,
	IRenderDoneObserver,
	ILogger,
	LoggerSymbol,
	IViewMode,
} from '@wix/thunderbolt-symbols'
import { INavigationInfo } from 'ds-feature-editor-navigation'
import { INavigationManager, NavigationManagerSymbol } from 'feature-navigation-manager'
import { createPromise } from '@wix/thunderbolt-commons'
import { Catharsis, CatharsisSymbol } from '@wix/thunderbolt-catharsis'

const viewModeFactory = (
	{
		viewMode: viewModeSetter,
		structure: structureSetters,
		deviceType: deviceTypeSetter,
		publicModel: publicModelSetter,
		$runInBatch,
	}: DSCarmi,
	renderDoneObserver: IRenderDoneObserver,
	{ clearLoadedPages }: INavigationInfo,
	logger: ILogger,
	navigationManager: INavigationManager,
	catharsis: Catharsis
): IViewMode => {
	const state: EditorViewModeState = {
		viewMode: 'desktop',
		wantedViewMode: 'desktop',
		switchViewModeDoneSubscribers: [],
		nextSwitchViewModeDoneSubscribers: [],
		nextViewModeSwitchIntent: Promise.resolve(),
	}

	const notifySubscribersOnSwitchViewModeDone = () => {
		state.switchViewModeDoneSubscribers.forEach((cb) => cb())
		state.nextSwitchViewModeDoneSubscribers.forEach((cb) => cb())
		state.nextSwitchViewModeDoneSubscribers = []
	}

	const changeViewMode = async (newViewMode: ViewModeType) => {
		if (state.viewMode !== newViewMode) {
			state.wantedViewMode = newViewMode
			if (navigationManager.isDuringNavigation()) {
				await navigationManager.waitForNavigationEnd()
			}
			const { promise: renderDonePromise, resolver: renderDoneResolver } = createPromise()

			renderDoneObserver.subscribe(
				() => {
					state.viewMode = newViewMode
					logger.setGlobalsForErrors({ tags: { viewMode: newViewMode } })
					renderDoneResolver()
					notifySubscribersOnSwitchViewModeDone()
				},
				{ once: true },
				false
			)

			clearLoadedPages()

			// Stop rendering the application until end of navigation to the changed view mode.
			navigationManager.setShouldBlockRender(true)

			try {
				$runInBatch(() => {
					const deviceType = newViewMode === 'mobile' ? 'Smartphone' : 'Desktop'

					structureSetters.setEntireStructure(getBaseStructure())
					deviceTypeSetter.setDeviceType(deviceType)
					publicModelSetter.setDeviceType(deviceType)
					viewModeSetter.setViewMode(newViewMode)
					catharsis.transaction(({ updateEnvironment }) =>
						updateEnvironment({ viewMode: newViewMode, deviceType })
					)
				})
				await renderDonePromise
			} catch (error) {
				logger.captureError(error, {
					tags: { feature: 'view-mode', methodName: 'changeViewMode', dsCarmiError: true },
				})
				renderDoneResolver()
				throw error
			}
		}
	}

	return {
		changeViewMode,
		getWantedIsMobileView: () => state.wantedViewMode === 'mobile',
		isMobileView: () => state.viewMode === 'mobile',
		isDuringViewModeSwitch: () => state.wantedViewMode !== state.viewMode,
		registerToSwitchViewModeDone: (handler) => state.switchViewModeDoneSubscribers.push(handler),
		registerToNextSwitchViewModeDone: (handler) => state.nextSwitchViewModeDoneSubscribers.push(handler),
		getCurrentViewMode: () => state.viewMode,
		getWantedViewMode: () => state.wantedViewMode,
		setNextViewModeSwitchIntent: (promise: Promise<void>) => (state.nextViewModeSwitchIntent = promise),
		getNextViewModeSwitchIntent: () => state.nextViewModeSwitchIntent,
	}
}
export const ViewMode = withDependencies(
	[
		CarmiInstance,
		RenderDoneObserverSymbol,
		NavigationInfoSymbol,
		LoggerSymbol,
		NavigationManagerSymbol,
		CatharsisSymbol,
	],
	viewModeFactory
)
